import React, { useState, useEffect, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import "./PagesCss/CreditManagement.css";
import article from "./Content";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const CreditCardPayoffCalculator = () => {
  const content = article.find((item) => item.id === 6);
  const [cards, setCards] = useState([
    { 
      id: Date.now(),
      name: "Credit Card 1", 
      balance: "5000", 
      apr: "18.99", 
      minPayment: "125", 
      monthlyPayment: "200", 
      extraPayment: "50", 
      promoAPR: "",
      promoEndDate: "",
      paymentStrategy: "avalanche"
    }
  ]);

  const [paymentFrequency, setPaymentFrequency] = useState("Monthly");
  const [startDate, setStartDate] = useState("");
  const [results, setResults] = useState(null);
  const [totalSavings, setTotalSavings] = useState(0);
  const [showChart, setShowChart] = useState(true);
  const [paymentStrategy, setPaymentStrategy] = useState("avalanche");
  const [totalDebt, setTotalDebt] = useState(0);
  const [totalMonthlyPayment, setTotalMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayoffTime, setTotalPayoffTime] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("cards"); // Mobile tabs
  const resultsRef = useRef();

  // Set default start date to today
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setStartDate(today);
  }, []);

  // Calculate totals whenever cards change
  useEffect(() => {
    const debt = cards.reduce((sum, card) => sum + (parseFloat(card.balance) || 0), 0);
    const monthly = cards.reduce((sum, card) => {
      const monthly = parseFloat(card.monthlyPayment) || parseFloat(card.minPayment) || 0;
      const extra = parseFloat(card.extraPayment) || 0;
      return sum + monthly + extra;
    }, 0);
    
    setTotalDebt(debt);
    setTotalMonthlyPayment(monthly);
    
    if (cards.every(card => card.balance && card.apr && (card.minPayment || card.monthlyPayment))) {
      setTimeout(() => {
        calculatePayoff();
      }, 100);
    }
  }, [cards]);

  const addCard = () => {
    const newCard = {
      id: Date.now(),
      name: `Credit Card ${cards.length + 1}`,
      balance: "3000",
      apr: "22.99",
      minPayment: "75",
      monthlyPayment: "150",
      extraPayment: "25",
      promoAPR: "",
      promoEndDate: "",
      paymentStrategy: "avalanche"
    };
    setCards([...cards, newCard]);
    setActiveCardIndex(cards.length);
  };

  const removeCard = (id) => {
    if (cards.length > 1) {
      const newCards = cards.filter(card => card.id !== id);
      setCards(newCards);
      setActiveCardIndex(0);
    }
  };

  const handleCardChange = (id, field, value) => {
    const newCards = cards.map(card => 
      card.id === id ? { ...card, [field]: value } : card
    );
    setCards(newCards);
  };

  const resetCalculator = () => {
    setCards([
      { 
        id: Date.now(),
        name: "Credit Card 1", 
        balance: "5000", 
        apr: "18.99", 
        minPayment: "125", 
        monthlyPayment: "200", 
        extraPayment: "50", 
        promoAPR: "",
        promoEndDate: "",
        paymentStrategy: "avalanche"
      }
    ]);
    setResults(null);
    setTotalSavings(0);
    setActiveCardIndex(0);
    setStartDate(new Date().toISOString().split('T')[0]);
  };

  const printResults = () => {
    window.print();
  };

  const generatePDF = () => {
    alert("PDF generation would be implemented here with a library like jsPDF");
    window.print();
  };

  const calculatePayoff = () => {
    const validCards = cards.filter(card => 
      card.balance && card.apr && (card.monthlyPayment || card.minPayment)
    );

    if (validCards.length === 0) {
      alert("Please fill in required fields for at least one card (Balance, APR, and either Monthly Payment or Minimum Payment)");
      return;
    }

    let payoffResults = [];
    
    if (paymentStrategy === "snowball" || paymentStrategy === "avalanche") {
      payoffResults = calculateDebtStrategy(paymentStrategy);
    } else {
      payoffResults = validCards.map(card => calculateSingleCardPayoff(card));
    }

    const totalInterestPaid = payoffResults.reduce((sum, res) => sum + (parseFloat(res.totalInterest) || 0), 0);
    const maxPayoffTime = Math.max(...payoffResults.map(res => res.months || 0));
    
    setTotalInterest(totalInterestPaid);
    setTotalPayoffTime(maxPayoffTime);

    const minPaymentResults = validCards.map(card => {
      const balance = parseFloat(card.balance) || 0;
      const apr = (parseFloat(card.apr) || 0) / 100;
      const minPayment = parseFloat(card.minPayment) || 0;

      if (!balance || !minPayment || !apr) return null;

      let remaining = balance;
      let months = 0;
      let totalInterest = 0;

      while (remaining > 0 && months < 600) {
        const interest = remaining * (apr / 12);
        totalInterest += interest;
        const principalPayment = Math.min(minPayment - interest, remaining);
        remaining -= principalPayment;
        months++;
        if (remaining <= 0) break;
      }

      return months;
    });

    let savings = 0;
    payoffResults.forEach((res, idx) => {
      if (res && minPaymentResults[idx]) {
        const minPayment = parseFloat(validCards[idx].minPayment || validCards[idx].monthlyPayment || 0);
        savings += (minPaymentResults[idx] - res.months) * minPayment;
      }
    });

    setTotalSavings(savings);
    setResults(payoffResults);
  };

  const calculateSingleCardPayoff = (card) => {
    const balance = parseFloat(card.balance) || 0;
    const apr = (parseFloat(card.apr) || 0) / 100;
    const monthlyPayment = parseFloat(card.monthlyPayment) || parseFloat(card.minPayment) || 0;
    const extraPayment = parseFloat(card.extraPayment) || 0;
    const totalMonthlyPayment = monthlyPayment + extraPayment;

    if (!balance || !totalMonthlyPayment || !apr) return null;

    let remaining = balance;
    let months = 0;
    let totalInterest = 0;
    const monthlyData = [];
    const interestData = [];

    while (remaining > 0 && months < 600) {
      const interest = remaining * (apr / 12);
      totalInterest += interest;
      const principalPayment = Math.min(totalMonthlyPayment - interest, remaining);
      remaining -= principalPayment;
      
      months++;
      monthlyData.push(remaining > 0 ? remaining : 0);
      interestData.push(interest);
      
      if (remaining <= 0) break;
    }

    return {
      name: card.name,
      months,
      totalInterest: totalInterest.toFixed(2),
      totalPayment: (balance + totalInterest).toFixed(2),
      monthlyData,
      interestData,
      originalPayoff: months
    };
  };

  const calculateDebtStrategy = (strategy) => {
    const validCards = cards.filter(card => 
      card.balance && card.apr && (card.monthlyPayment || card.minPayment)
    ).map(card => ({
      ...card,
      balance: parseFloat(card.balance),
      apr: parseFloat(card.apr) / 100,
      minPayment: parseFloat(card.minPayment) || parseFloat(card.monthlyPayment) || 0,
      extraPayment: parseFloat(card.extraPayment) || 0
    }));

    const sortedCards = [...validCards].sort((a, b) => {
      if (strategy === "snowball") {
        return a.balance - b.balance;
      } else {
        return b.apr - a.apr;
      }
    });

    const results = [];
    let availableExtra = validCards.reduce((sum, card) => sum + card.extraPayment, 0);
    
    sortedCards.forEach((card, index) => {
      let remaining = card.balance;
      let months = 0;
      let totalInterest = 0;
      const monthlyData = [];
      const interestData = [];

      let cardPayment = card.minPayment + card.extraPayment;
      
      if (index > 0) {
        cardPayment += availableExtra;
      }

      while (remaining > 0 && months < 600) {
        const interest = remaining * (card.apr / 12);
        totalInterest += interest;
        const principalPayment = Math.min(cardPayment - interest, remaining);
        remaining -= principalPayment;
        
        months++;
        monthlyData.push(remaining > 0 ? remaining : 0);
        interestData.push(interest);
        
        if (remaining <= 0) break;
      }

      results.push({
        name: card.name,
        months,
        totalInterest: totalInterest.toFixed(2),
        totalPayment: (card.balance + totalInterest).toFixed(2),
        monthlyData,
        interestData,
        originalPayoff: months,
        strategy: paymentStrategy
      });
    });

    return results;
  };

  // Chart data configurations
  const payoffChartData = {
    labels: results?.map(res => res?.name).filter(Boolean) || [],
    datasets: [
      {
        label: 'Payoff Time (Months)',
        data: results?.map(res => res?.months).filter(Boolean) || [],
        backgroundColor: '#A03B00',
        borderColor: '#A03B00',
        borderWidth: 1,
      },
    ],
  };

  const debtDistributionData = {
    labels: cards.map(card => card.name),
    datasets: [
      {
        label: 'Balance',
        data: cards.map(card => parseFloat(card.balance) || 0),
        backgroundColor: ['#A03B00', '#F06201', '#FF8C42', '#FFB088', '#FFD1B8'],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  };

  const consolidatedPaymentBreakdownData = {
    labels: cards.map(card => card.name),
    datasets: [
      {
        label: 'Principal Balance',
        data: cards.map(card => parseFloat(card.balance) || 0),
        backgroundColor: '#F06201',
        borderWidth: 2,
        borderColor: '#fff',
      },
      {
        label: 'Estimated Interest',
        data: results ? cards.map((card, index) => parseFloat(results[index]?.totalInterest || 0)) : Array(cards.length).fill(0),
        backgroundColor: '#A03B00',
        borderWidth: 2,
        borderColor: '#fff',
      }
    ],
  };

  const consolidatedChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${label}: $${value.toFixed(2)} (${percentage}%)`;
          }
        }
      }
    },
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
    },
  };

  return (
    <div className="calculator-container">
      <header className="calculator-header">
        <div className="header-content">
          <h1>Credit Card Payoff Calculator</h1>
          <p>Plan your debt-free journey with smart payment strategies</p>
          <div className="debt-summary">
            <div className="summary-item">
              <span>Total Debt:</span>
              <strong>${totalDebt.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Monthly Payment:</span>
              <strong>${totalMonthlyPayment.toFixed(2)}</strong>
            </div>
            <div className="summary-item">
              <span>Cards:</span>
              <strong>{cards.length}</strong>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Tabs Navigation */}
      <div className="mobile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'cards' ? 'active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          <i className="fa-solid fa-credit-card"></i>
          Cards
        </button>
        <button 
          className={`tab-btn ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <i className="fa-solid fa-chart-pie"></i>
          Results
        </button>
      </div>

      <div className="calculator-layout">
        {/* Left Side - Credit Cards */}
        <div className={`left-panel ${activeTab === 'cards' || activeTab === 'settings' ? 'active' : ''}`}>
          <div className="cards-section">
            <div className="section-header">
              <h2>Your Credit Cards</h2>
              <div className="card-actions">
                <button className="btn-secondary" onClick={addCard}>
                  + Add Card
                </button>
                <button className="btn-reset" onClick={resetCalculator}>
                  Reset All
                </button>
              </div>
            </div>

            <div className="strategy-selector">
              <div className="strategy-header">
                <label>Payment Strategy:</label>
                <select 
                  value={paymentStrategy} 
                  onChange={(e) => setPaymentStrategy(e.target.value)}
                >
                  <option value="individual">Individual Cards</option>
                  <option value="avalanche">Avalanche (Highest APR First)</option>
                  <option value="snowball">Snowball (Smallest Balance First)</option>
                </select>
              </div>
              <div className="strategy-info">
                {paymentStrategy === "avalanche" && (
                  <>
                    <i className="fa-solid fa-lightbulb"></i>
                    {" "}Pay off highest interest rate cards first to save the most money
                  </>
                )}
                {paymentStrategy === "snowball" && (
                  <>
                    <i className="fa-solid fa-bullseye"></i>
                    {" "}Pay off smallest balances first for psychological wins
                  </>
                )}
                {paymentStrategy === "individual" && (
                  <>
                    <i className="fa-solid fa-chart-column"></i>
                    {" "}Pay each card according to its individual payment plan
                  </>
                )}
              </div>
            </div>

            {/* Card Navigation Tabs */}
            {cards.length > 1 && (
              <div className="card-navigation">
                {cards.map((card, index) => (
                  <button
                    key={card.id}
                    className={`nav-tab ${activeCardIndex === index ? 'active' : ''}`}
                    onClick={() => setActiveCardIndex(index)}
                  >
                    {card.name}
                  </button>
                ))}
              </div>
            )}

            <div className="cards-container">
              {cards.map((card, index) => (
                <div 
                  key={card.id} 
                  className={`card-input-group ${activeCardIndex === index ? 'active' : ''}`}
                >
                  <div className="card-header">
                    <h3>{card.name}</h3>
                    {cards.length > 1 && (
                      <button 
                        className="btn-remove" 
                        onClick={() => removeCard(card.id)}
                        title="Remove card"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  
                  <div className="input-grid">
                    <div className="input-group">
                      <label>Current Balance ($)*</label>
                      <input
                        type="number"
                        value={card.balance}
                        onChange={(e) => handleCardChange(card.id, "balance", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>APR (%)*</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.apr}
                        onChange={(e) => handleCardChange(card.id, "apr", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Minimum Payment ($)*</label>
                      <input
                        type="number"
                        value={card.minPayment}
                        onChange={(e) => handleCardChange(card.id, "minPayment", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Monthly Payment ($)</label>
                      <input
                        type="number"
                        value={card.monthlyPayment}
                        onChange={(e) => handleCardChange(card.id, "monthlyPayment", e.target.value)}
                        placeholder="Same as minimum"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Extra Payments ($)</label>
                      <input
                        type="number"
                        value={card.extraPayment}
                        onChange={(e) => handleCardChange(card.id, "extraPayment", e.target.value)}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                    
                    <div className="input-group">
                      <label>Promotional APR (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={card.promoAPR}
                        onChange={(e) => handleCardChange(card.id, "promoAPR", e.target.value)}
                        placeholder="Optional"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                  <div className="required-note">* Required fields</div>
                </div>
              ))}
            </div>
          </div>

          <div className="payment-settings">
            <div className="section-header">
              <h2>Payment Settings</h2>
            </div>
            <div className="settings-grid">
              <div className="input-group">
                <label>Payment Frequency</label>
                <select 
                  value={paymentFrequency} 
                  onChange={(e) => setPaymentFrequency(e.target.value)}
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Bi-weekly">Bi-weekly</option>
                  <option value="Weekly">Weekly</option>
                </select>
              </div>

              <div className="input-group">
                <label>Start Date</label>
                <input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
              </div>
            </div>
          </div>

          <div className="actions-section">
            <button className="btn-primary" onClick={calculatePayoff}>
              Calculate Payoff Plan
            </button>
          </div>

          <div className="default-results">
              <h2>Your Payoff Results</h2>
              <div className="default-message">
                <p>Fill in your credit card information and click "Calculate Payoff Plan" to see your personalized payoff strategy.</p>
                <div className="default-tips">
                  <h3><i className="fa-solid fa-lightbulb"></i> Tips for Faster Payoff:</h3>
                  <ul>
                    <li>Pay more than the minimum payment whenever possible</li>
                    <li>Consider using the avalanche method to save on interest</li>
                    <li>Make bi-weekly payments to reduce interest accumulation</li>
                    <li>Use windfalls (tax returns, bonuses) to make extra payments</li>
                  </ul>
                </div>
              </div>
            </div>
        </div>

        {/* Right Side - Payoff Results */}
        <div className={`right-panel ${activeTab === 'results' ? 'active' : ''}`} ref={resultsRef}>
          {results ? (
            <div className="results-section">
              <div className="section-header">
                <h2>Your Payoff Results</h2>
                <div className="results-actions">
                  <div className="chart-toggle">
                    <button 
                      className={`toggle-btn ${showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(true)}
                    >
                      <i className="fa-solid fa-chart-column"></i> Charts
                    </button>
                    <button 
                      className={`toggle-btn ${!showChart ? 'active' : ''}`}
                      onClick={() => setShowChart(false)}
                    >
                      <i className="fa-solid fa-clipboard-list"></i> Details
                    </button>
                  </div>
                  <div className="button-group">
  <button className="btn-secondary" onClick={printResults}>
    <i className="fa-solid fa-print"></i> Print
  </button>
  <button className="btn-secondary" onClick={generatePDF}>
    <i className="fa-regular fa-file-lines"></i> Save as PDF
  </button>
</div>
                </div>
              </div>

              {/* Results Summary */}
              <div className="results-summary">
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-calendar-days"></i></div>
                  <h3>Total Payoff Time</h3>
                  <div className="summary-value">{totalPayoffTime} months</div>
                  <div className="summary-note">Until all cards are paid off</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-coins"></i></div>
                  <h3>Total Interest</h3>
                  <div className="summary-value">${totalInterest.toFixed(2)}</div>
                  <div className="summary-note">Interest you'll pay</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-piggy-bank"></i></div>
                  <h3>Total Savings</h3>
                  <div className="summary-value savings">${totalSavings.toFixed(2)}</div>
                  <div className="summary-note">Compared to minimum payments</div>
                </div>
                <div className="summary-card">
                  <div className="summary-icon"><i className="fa-solid fa-credit-card"></i></div>
                  <h3>Total Debt</h3>
                  <div className="summary-value">${totalDebt.toFixed(2)}</div>
                  <div className="summary-note">Combined balance from all cards</div>
                </div>
              </div>

              {showChart ? (
                <div className="charts-grid">
                  <div className="chart-card">
                    <h3>Payoff Timeline</h3>
                    <Bar data={payoffChartData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Payoff Timeline:</strong> Shows how many months each credit card will take to pay off completely.</p>
                    </div>
                  </div>
                  
                  <div className="chart-card">
                    <h3>Debt Distribution</h3>
                    <Doughnut data={debtDistributionData} options={chartOptions} />
                    <div className="chart-description">
                      <p><strong>Debt Distribution:</strong> Visualizes how your total debt is distributed across different credit cards.</p>
                    </div>
                  </div>

                  <div className="chart-card">
                    <h3>Payment Breakdown - All Cards</h3>
                    <Bar data={consolidatedPaymentBreakdownData} options={consolidatedChartOptions} />
                    <div className="chart-description">
                      <p><strong>Payment Breakdown:</strong> Shows the principal balance vs estimated interest for each credit card.</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="results-details">
                  <div className="results-grid">
                    {results.map((res, idx) =>
                      res ? (
                        <div key={idx} className="result-card">
                          <h3>{res.name}</h3>
                          <div className="result-stats">
                            <div className="stat">
                              <label>Payoff Time</label>
                              <span className="stat-value">{res.months} months</span>
                            </div>
                            <div className="stat">
                              <label>Total Interest</label>
                              <span className="stat-value">${res.totalInterest}</span>
                            </div>
                            <div className="stat">
                              <label>Total Payment</label>
                              <span className="stat-value">${res.totalPayment}</span>
                            </div>
                            <div className="stat">
                              <label>Monthly Payment</label>
                              <span className="stat-value">
                                ${(parseFloat(cards[idx].monthlyPayment || cards[idx].minPayment) + parseFloat(cards[idx].extraPayment || 0)).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div key={idx} className="result-card error">
                          <p>Incomplete data for {cards[idx].name}</p>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="no-results">
              <h3>No results yet</h3>
              <p>Enter your details and click "Calculate Interest" to view results.</p>
            </div>
          )}
        </div>
      </div>
      {content && content.content}
    </div>
  );
};

export default CreditCardPayoffCalculator;